import { UPSTOX_INSTRUMENT_KEYS, UPSTOX_CANDLE_API } from '../config/constants'
import { formatDateToYYYYMMDD } from '../lib/date-utils'
import { getOrWaitForAuthToken } from './firebase'

export interface UpstoxCandle {
    timestamp: string
    open: number
    high: number
    low: number
    close: number
    volume: number
    oi: number
}

export interface CandleResponse {
    status: string
    data: {
        candles: (string | number)[][]
    }
}

// ============================================================================
// In-memory cache for candle data
// Historical (day/week/month) rarely changes — cache for 30 min
// Intraday (1minute etc.) changes often — cache for 60 seconds
// ============================================================================
const CANDLE_CACHE = new Map<string, { data: UpstoxCandle[]; fetchedAt: number }>()
const IN_FLIGHT_CANDLES = new Map<string, Promise<UpstoxCandle[]>>()
const RATE_LIMIT_COOLDOWNS = new Map<string, number>()

const CACHE_TTL: Record<string, number> = {
    day: 60 * 60 * 1000, // 60 minutes
    week: 60 * 60 * 1000, // 60 minutes
    month: 60 * 60 * 1000, // 60 minutes
    '1minute': 60 * 1000, // 60 seconds
    '3minute': 60 * 1000,
    '5minute': 60 * 1000,
    '15minute': 60 * 1000,
    '30minute': 60 * 1000,
    '60minute': 5 * 60 * 1000, // 5 minutes
}

function getCacheTTL(interval: string): number {
    return CACHE_TTL[interval] || 60 * 1000
}

// ============================================================================
// CLIENT-SIDE CONCURRENCY QUEUE & PACING
// Prevents blasting Upstox candle endpoints with 50+ simultaneous requests.
// Max concurrent: 3, with 30ms pacing between requests.
// ============================================================================
const MAX_CONCURRENT_PROXY_REQUESTS = 3
let activeProxyRequests = 0
const proxyQueue: Array<() => void> = []

function enqueueProxyRequest<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        const execute = async () => {
            activeProxyRequests++
            try {
                const result = await fn()
                resolve(result)
            } catch (err) {
                reject(err)
            } finally {
                activeProxyRequests--
                setTimeout(() => {
                    if (proxyQueue.length > 0) {
                        const next = proxyQueue.shift()
                        next?.()
                    }
                }, 30)
            }
        }

        if (activeProxyRequests < MAX_CONCURRENT_PROXY_REQUESTS) {
            execute()
        } else {
            proxyQueue.push(execute)
        }
    })
}

// In-memory cache & in-flight deduplicator for raw proxy endpoint responses
const PROXY_RAW_CACHE = new Map<string, { data: CandleResponse; timestamp: number }>()
const PROXY_IN_FLIGHT = new Map<string, Promise<CandleResponse>>()

/**
 * Fetch candles via the /api/candles-proxy endpoint (no tRPC).
 * Uses in-memory cache and client concurrency queue to prevent redundant network requests.
 */
async function fetchCandlesFromProxy(
    instrumentKey: string,
    interval: string,
    type: 'historical' | 'intraday',
    from?: string,
    to?: string,
): Promise<CandleResponse> {
    const rawCacheKey = `${instrumentKey}:${interval}:${type}:${from || ''}:${to || ''}`
    const rawCached = PROXY_RAW_CACHE.get(rawCacheKey)
    const rawTtl = type === 'historical' ? 10 * 60 * 1000 : 30 * 1000
    if (rawCached && Date.now() - rawCached.timestamp < rawTtl) {
        return rawCached.data
    }

    if (PROXY_IN_FLIGHT.has(rawCacheKey)) {
        return PROXY_IN_FLIGHT.get(rawCacheKey)!
    }

    const fetchPromise = enqueueProxyRequest(async (): Promise<CandleResponse> => {
        const cooldownUntil = RATE_LIMIT_COOLDOWNS.get(instrumentKey) || 0
        if (Date.now() < cooldownUntil) {
            if (rawCached) return rawCached.data
            return { status: 'error', data: { candles: [] } } as any
        }

        const params = new URLSearchParams({
            instrumentKey,
            interval,
            type,
        })
        if (from) params.set('from', from)
        if (to) params.set('to', to)

        try {
            const token = await getOrWaitForAuthToken()

            const resp = await fetch(`/api/candles-proxy?${params}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            })

            if (resp.status === 429) {
                // Short cooldown (6s) instead of 60s
                console.warn(`[CandleService] Rate limited (429) for ${instrumentKey}. Backing off for 6s.`)
                RATE_LIMIT_COOLDOWNS.set(instrumentKey, Date.now() + 6000)
                if (rawCached) return rawCached.data
                return { status: 'error', data: { candles: [] } } as any
            }

            const contentType = resp.headers.get('content-type') || ''
            if (resp.ok && contentType.includes('application/json')) {
                const json = await resp.json()
                if (json && (json.status === 'success' || Array.isArray(json.data?.candles))) {
                    const candleResp = json as CandleResponse
                    PROXY_RAW_CACHE.set(rawCacheKey, { data: candleResp, timestamp: Date.now() })
                    return candleResp
                }
            }
        } catch (err: any) {
            // Handled below
        }

        if (rawCached) return rawCached.data
        return { status: 'error', data: { candles: [] } } as any
    }).finally(() => {
        PROXY_IN_FLIGHT.delete(rawCacheKey)
    })

    PROXY_IN_FLIGHT.set(rawCacheKey, fetchPromise)
    return fetchPromise
}

/**
 * Fetch historical candles via the server-side proxy to avoid CORS.
 * Uses in-memory cache to prevent redundant network requests.
 * Default: 30 days (1 month) of history.
 */
export const fetchHistoricalCandles = async (
    symbol: string,
    days: number = 30,
    interval: string = '1minute',
    explicitInstrumentKey?: string,
    targetDateStr?: string | null,
): Promise<UpstoxCandle[]> => {
    // Check cache first
    const cacheKey = `${symbol}:${interval}:${days}:${explicitInstrumentKey || 'index'}:${targetDateStr || 'live'}`
    const cached = CANDLE_CACHE.get(cacheKey)
    if (cached && Date.now() - cached.fetchedAt < getCacheTTL(interval)) {
        return cached.data
    }

    // Deduplicate in-flight requests
    if (IN_FLIGHT_CANDLES.has(cacheKey)) {
        return IN_FLIGHT_CANDLES.get(cacheKey)!
    }

    const fetchPromise = (async (): Promise<UpstoxCandle[]> => {
        try {
            const instrumentKey = explicitInstrumentKey || UPSTOX_INSTRUMENT_KEYS[symbol] || `NSE_INDEX|${symbol}`

        // Cap days to prevent 400 Bad Request from upstream Upstox API
        let effectiveDays = days
        if (interval.includes('minute')) {
            effectiveDays = Math.max(effectiveDays, 5)
            if (effectiveDays > 10) effectiveDays = 10
        } else if (effectiveDays > 365) {
            effectiveDays = 365
        }

        // 1. Fetch Historical Range (Past Data) via server proxy
        const toDate = targetDateStr ? new Date(targetDateStr) : new Date()
        if (targetDateStr) {
            toDate.setHours(23, 59, 59, 999) // Include the entire target day
        }
        
        const fromDate = new Date(toDate.getTime())
        // Convert requested trading days to sufficient calendar days (accounting for weekends and holidays)
        const calendarDays = interval === 'day'
            ? Math.ceil(effectiveDays * 1.6) + 4
            : effectiveDays
        fromDate.setDate(toDate.getDate() - calendarDays)

        const toDateStr = formatDateToYYYYMMDD(toDate)
        const fromDateStr = formatDateToYYYYMMDD(fromDate)

        // Enable intraday (today's live data) for minute-level intervals
        const isIntradayInterval = interval.includes('minute') || interval === '60minute'

        const promises: Promise<CandleResponse>[] = [
            fetchCandlesFromProxy(instrumentKey, interval, 'historical', fromDateStr, toDateStr),
        ]

        if (isIntradayInterval && !targetDateStr) {
            promises.push(fetchCandlesFromProxy(instrumentKey, interval, 'intraday'))
        }

        // Run in parallel
        const results = await Promise.allSettled(promises)
        const historicalRes = results[0]
        const intradayRes =
            isIntradayInterval && results[1]
                ? results[1]
                : ({ status: 'rejected', reason: 'Skipped Intraday' } as PromiseSettledResult<CandleResponse>)

        let allCandles: (string | number)[][] = []

        // Process Historical
        if (historicalRes && historicalRes.status === 'fulfilled') {
            const val = (historicalRes as any).value
            if (val?.status === 'success' && val?.data?.candles && Array.isArray(val.data.candles)) {
                allCandles = [...allCandles, ...val.data.candles]
            }
        }

        // Process Intraday
        if (intradayRes && intradayRes.status === 'fulfilled') {
            const val = intradayRes.value
            if (val?.status === 'success' && val?.data?.candles && Array.isArray(val.data.candles)) {
                allCandles = [...allCandles, ...val.data.candles]
            }
        }

        if (allCandles.length === 0) {
            if (cached && cached.data.length > 0) {
                return cached.data
            }
            // Negative cache for 30s to prevent retry storm
            CANDLE_CACHE.set(cacheKey, { data: [], fetchedAt: Date.now() })
            return []
        }

        // Deduplicate and Sort
        const candleMap = new Map<string, (string | number)[]>()

        allCandles.forEach((c) => {
            const ts = c[0] as string
            candleMap.set(ts, c)
        })

        const sortedCandles = Array.from(candleMap.values())
            .map((c) => ({
                timestamp: c[0] as string,
                open: c[1] as number,
                high: c[2] as number,
                low: c[3] as number,
                close: c[4] as number,
                volume: c[5] as number,
                oi: c[6] as number,
            }))
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

        // Store in cache before returning
        CANDLE_CACHE.set(cacheKey, { data: sortedCandles, fetchedAt: Date.now() })

        return sortedCandles
    } catch (error: any) {
        console.error('[CandleService] Failed to fetch historical candles:', error)
        if (cached && cached.data.length > 0) {
            return cached.data
        }
        // Negative cache for 60s to prevent retry storm
        CANDLE_CACHE.set(cacheKey, { data: [], fetchedAt: Date.now() })
        return []
    } finally {
        IN_FLIGHT_CANDLES.delete(cacheKey)
    }
})()

IN_FLIGHT_CANDLES.set(cacheKey, fetchPromise)
return fetchPromise
}
